import { z } from 'zod/v4'

import {
  OverrideType,
  RateLimitRole,
  ViolationSeverity,
} from '../../contracts/admin/rate-limiting.types.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Límites de uso»: the global limits, the organisation overrides and the latest violations in one answer. Each row
 * mirrors the interface the services return (`GlobalRateLimit`, `TenantOverride`, `RateLimitViolation`); the type
 * assertions of `packages/backend/src/platform/server/__tests__/shared-rate-limiting-screen.type-assertions.ts` tie them
 * in both directions. The enumeration schemas are exported so the backend mappers check the text of the database.
 */

export const adminRateLimitRoleSchema = z.enum(RateLimitRole)
export const adminOverrideTypeSchema = z.enum(OverrideType)
export const adminViolationSeveritySchema = z.enum(ViolationSeverity)

export const adminGlobalRateLimitRowSchema = z.strictObject({
  id: z.string(),
  role: adminRateLimitRoleSchema,
  requests_per_minute: z.number(),
  requests_per_hour: z.number(),
  requests_per_day: z.number(),
  block_duration_minutes: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
})

/**
 * The three values are ABSENT when the database gives `null`, as in today's route: the edit modals read
 * `TenantOverride`.
 */
export const adminTenantOverrideRowSchema = z.strictObject({
  id: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string().optional(),
  override_type: adminOverrideTypeSchema,
  value_multiplier: z.number().optional(),
  value_fixed: z.number().optional(),
  value_percentage: z.number().optional(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

/**
 * `tenant_name` and `user_email` are `null` when the violation is anonymous: the `LEFT JOIN` gives `null` even though
 * `RateLimitViolation` says `?:`.
 */
export const adminRateLimitViolationRowSchema = z.strictObject({
  id: z.string(),
  tenant_id: z.string().nullable(),
  tenant_name: z.string().nullable(),
  user_id: z.string().nullable(),
  user_email: z.string().nullable(),
  endpoint: z.string(),
  ip_address: z.string(),
  detected_at: z.string(),
  severity: adminViolationSeveritySchema,
  request_count: z.number(),
  limit: z.number(),
  window: z.string(),
})

export const adminRateLimitingQuerySchema = z.strictObject({
  violationsSeverity: adminViolationSeveritySchema.optional(),
})

export const adminRateLimitingScreen = defineScreen({
  id: 'admin.rateLimiting',
  path: '/api/screens/admin/rate-limiting',
  access: 'super_admin',
  query: adminRateLimitingQuerySchema,
  response: z.strictObject({
    globalLimits: screenSection(z.array(adminGlobalRateLimitRowSchema)),
    overrides: screenSection(z.array(adminTenantOverrideRowSchema)),
    violations: screenSection(
      z.strictObject({ items: z.array(adminRateLimitViolationRowSchema), total: z.number() }),
    ),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.rateLimiting'],
  budget: { requests: 1, statements: 5, bytes: 80_000, p95Ms: 300 },
  summary:
    'API rate limits: the global limits, the organization overrides and the latest 50 violations',
})

export type AdminGlobalRateLimitRow = z.infer<typeof adminGlobalRateLimitRowSchema>
export type AdminTenantOverrideRow = z.infer<typeof adminTenantOverrideRowSchema>
export type AdminRateLimitViolationRow = z.infer<typeof adminRateLimitViolationRowSchema>
export type AdminRateLimitingQuery = ScreenQuery<typeof adminRateLimitingScreen>
export type AdminRateLimitingData = ScreenData<typeof adminRateLimitingScreen>
